/**
 * SearchScreen — free-text query plus AND-combined filter chips.
 *
 * Filters supported:
 *   - kind:  全部 / 支出 / 收入
 *   - category: pick one category id
 *   - book: pick one book id
 *   - date_range: 本月 / 上月 / 過去 3 個月 / 自訂
 *   - amount: operator (> < >= <= =) + amount, compared in primary currency
 *
 * Free-text query searches `note`, `project`, `tags`, plus the category
 * and subcategory NAME of each transaction — so 「電費」 matches a row
 * whose subcategory is named 電費 even if the note is empty.
 *
 * Empty-state surfaces (no query / no chips):
 *   - 最近搜尋 — last N free-text queries the user submitted
 *   - 已儲存的篩選 — named filter snapshots the user pinned
 *
 * Sharp edges:
 *   - Query is matched via `String.prototype.toLowerCase().includes()` —
 *     NEVER `new RegExp(rawInput)`. Building a regex from raw user input is
 *     a ReDoS hazard and would surprise users who type `.` or `(`.
 *   - Filters combine with AND (the spec is explicit: query 全家 + 食 should
 *     return ONLY 食 transactions whose note contains 全家).
 *   - Amount comparison FX-converts every row into the primary currency
 *     before comparing — a 日幣 1000 row (≈ NT$ 200) only matches `> 100`
 *     in TWD when the user's primary is TWD; we never compare native
 *     amounts directly.
 */

import { useEffect, useMemo, useState } from 'react';
import { cssColor } from '@/lib/colorToken';
import type {
  BookRow,
  CategoryRow,
  TransactionRow,
  TxKind,
} from '@/lib/sheets/types';
import { formatAmount } from '@/lib/format/currency';
import { convertMinor } from '@/lib/format/fx';
import { dayPart } from '@/lib/format/datetime';
import RecentTxList from '@/components/home/RecentTxList';
import BottomSheet from '@/components/common/BottomSheet';
import Icon from '@/components/common/Icon';
import {
  clearRecentSearches,
  deleteSavedFilter,
  loadRecentSearches,
  loadSavedFilters,
  pushRecentSearch,
  saveFilter,
  type AmountFilter,
  type SavedFilter,
} from '@/lib/searchHistory';
import type {
  AmountOp,
  DateRangeKey,
  KindFilter,
} from './searchFilters';

export type { KindFilter, DateRangeKey };

export interface SearchScreenProps {
  transactions: TransactionRow[];
  categories: CategoryRow[];
  books: BookRow[];
  /** User's primary display currency. All `amount` filter comparisons
   *  convert every row into this currency before comparing. */
  primaryCurrency: string;
  onTxClick?: (tx: TransactionRow) => void;
}

interface Filters {
  query: string;
  kind: KindFilter;
  categoryId: string | null;
  bookId: string | null;
  dateRange: DateRangeKey | null;
  amount: AmountFilter | null;
}

const INITIAL_FILTERS: Filters = {
  query: '',
  kind: 'all',
  categoryId: null,
  bookId: null,
  dateRange: null,
  amount: null,
};

const AMOUNT_OPS: ReadonlyArray<{ op: AmountOp; label: string }> = [
  { op: '>', label: '大於' },
  { op: '>=', label: '大於等於' },
  { op: '=', label: '等於' },
  { op: '<=', label: '小於等於' },
  { op: '<', label: '小於' },
];

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dateRangeBounds(key: DateRangeKey): [string, string] | null {
  const now = new Date();
  if (key === 'this_month') {
    const a = new Date(now.getFullYear(), now.getMonth(), 1);
    const b = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return [ymd(a), ymd(b)];
  }
  if (key === 'last_month') {
    const a = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const b = new Date(now.getFullYear(), now.getMonth(), 0);
    return [ymd(a), ymd(b)];
  }
  if (key === 'past_3_months') {
    const a = new Date(now.getFullYear(), now.getMonth() - 3, 1);
    return [ymd(a), ymd(now)];
  }
  return null; // custom — caller handles separately
}

function compareAmount(value: number, op: AmountOp, target: number): boolean {
  switch (op) {
    case '>': return value > target;
    case '>=': return value >= target;
    case '=': return value === target;
    case '<=': return value <= target;
    case '<': return value < target;
  }
}

interface FilterContext {
  categoryNameById: Map<string, string>;
  primaryCurrency: string;
}

function filter(
  transactions: TransactionRow[],
  f: Filters,
  ctx: FilterContext,
): TransactionRow[] {
  const q = f.query.trim().toLowerCase();

  const hasAny =
    q.length > 0 ||
    f.kind !== 'all' ||
    f.categoryId !== null ||
    f.bookId !== null ||
    f.dateRange !== null ||
    f.amount !== null;
  if (!hasAny) return [];

  const bounds =
    f.dateRange && f.dateRange !== 'custom' ? dateRangeBounds(f.dateRange) : null;

  return transactions.filter((t) => {
    if (q.length > 0) {
      const catName = ctx.categoryNameById.get(t.category_id) ?? '';
      const subName = ctx.categoryNameById.get(t.subcategory_id) ?? '';
      const haystack = [
        t.note,
        t.project,
        t.tags,
        catName,
        subName,
      ]
        .map((s) => (s || '').toLowerCase())
        .join('\n');
      if (!haystack.includes(q)) return false;
    }
    if (f.kind !== 'all' && t.kind !== f.kind) return false;
    if (f.categoryId && t.category_id !== f.categoryId) return false;
    if (f.bookId && t.book_id !== f.bookId) return false;
    if (bounds) {
      // v5: dayPart aligns datetime stamps against day-only boundaries.
      const day = dayPart(t.date);
      if (day < bounds[0] || day > bounds[1]) return false;
    }
    if (f.amount) {
      const minorInPrimary = convertMinor(
        t.amount_minor,
        t.currency || 'TWD',
        ctx.primaryCurrency,
      );
      const major = minorInPrimary / 100;
      if (!compareAmount(major, f.amount.op, f.amount.amount)) return false;
    }
    return true;
  });
}

export default function SearchScreen({
  transactions,
  categories,
  books,
  primaryCurrency,
  onTxClick,
}: SearchScreenProps): JSX.Element {
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [openSheet, setOpenSheet] = useState<
    | 'category'
    | 'book'
    | 'date_range'
    | 'amount'
    | 'save_filter'
    | null
  >(null);
  const [recent, setRecent] = useState<string[]>(() => loadRecentSearches());
  const [saved, setSaved] = useState<SavedFilter[]>(() => loadSavedFilters());

  const categoryNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of categories) m.set(c.id, c.name);
    return m;
  }, [categories]);

  const filterCtx: FilterContext = useMemo(
    () => ({ categoryNameById, primaryCurrency }),
    [categoryNameById, primaryCurrency],
  );

  const results = useMemo(() => filter(transactions, filters, filterCtx), [
    transactions,
    filters,
    filterCtx,
  ]);

  const hasAny =
    filters.query.length > 0 ||
    filters.kind !== 'all' ||
    filters.categoryId !== null ||
    filters.bookId !== null ||
    filters.dateRange !== null ||
    filters.amount !== null;

  const selectedCategory = filters.categoryId
    ? categories.find((c) => c.id === filters.categoryId)
    : null;
  const selectedBook = filters.bookId
    ? books.find((b) => b.id === filters.bookId)
    : null;

  // Persist the current query into recent-searches when the user commits
  // it (Enter or blur). Live keystrokes don't persist — that would litter
  // the list with prefixes.
  const commitRecent = (q: string): void => {
    const trimmed = q.trim();
    if (!trimmed) return;
    const next = pushRecentSearch(trimmed);
    setRecent(next);
  };

  // Reload saved/recent when the page mounts (in case another tab edited).
  useEffect(() => {
    setRecent(loadRecentSearches());
    setSaved(loadSavedFilters());
  }, []);

  const applyFilters = (next: Partial<Filters>): void => {
    setFilters((s) => ({ ...s, ...next }));
  };

  const applySavedFilter = (f: SavedFilter): void => {
    setFilters({
      query: f.filters.query ?? '',
      kind: f.filters.kind ?? 'all',
      categoryId: f.filters.categoryId ?? null,
      bookId: f.filters.bookId ?? null,
      dateRange: f.filters.dateRange ?? null,
      amount: f.filters.amount ?? null,
    });
  };

  const handleClearRecent = (): void => {
    clearRecentSearches();
    setRecent([]);
  };

  const handleSaveFilter = (name: string): void => {
    const entry = saveFilter(name, {
      query: filters.query,
      kind: filters.kind,
      categoryId: filters.categoryId,
      bookId: filters.bookId,
      dateRange: filters.dateRange,
      amount: filters.amount,
    });
    setSaved((prev) => [entry, ...prev]);
    setOpenSheet(null);
  };

  const handleDeleteSaved = (id: string): void => {
    const next = deleteSavedFilter(id);
    setSaved(next);
  };

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--cream-50)',
      }}
    >
      <div style={{ padding: '14px 16px 6px' }}>
        <input
          type="search"
          aria-label="搜尋關鍵字"
          autoComplete="off"
          placeholder="搜尋備註、分類、子分類或標籤"
          value={filters.query}
          onChange={(e) =>
            setFilters((s) => ({ ...s, query: e.target.value }))
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRecent(filters.query);
          }}
          onBlur={() => commitRecent(filters.query)}
          style={{
            width: '100%',
            padding: '12px 14px',
            border: '1px solid var(--border-1)',
            borderRadius: 14,
            background: 'var(--bg-surface)',
            font: '500 15px var(--font-cjk)',
            color: 'var(--fg-1)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      <div
        style={{
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          padding: '6px 16px 10px',
        }}
      >
        {/* kind segmented chip */}
        <div
          style={{
            display: 'inline-flex',
            background: 'var(--bg-raised)',
            padding: 3,
            borderRadius: 999,
            font: '500 12px var(--font-cjk)',
          }}
        >
          {(['all', 'expense', 'income'] as KindFilter[]).map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={filters.kind === k}
              data-testid={`chip-kind-${k}`}
              onClick={() => applyFilters({ kind: k })}
              style={{
                border: 0,
                background:
                  filters.kind === k ? 'var(--bg-surface)' : 'transparent',
                padding: '4px 12px',
                borderRadius: 999,
                color: filters.kind === k ? 'var(--fg-1)' : 'var(--fg-2)',
                cursor: 'pointer',
                font: 'inherit',
              }}
            >
              {k === 'all' ? '全部' : k === 'expense' ? '支出' : '收入'}
            </button>
          ))}
        </div>

        <Chip
          testId="chip-category"
          label={selectedCategory ? `分類：${selectedCategory.name}` : '分類'}
          active={!!selectedCategory}
          onClick={() => setOpenSheet('category')}
          onClear={
            selectedCategory
              ? () => applyFilters({ categoryId: null })
              : undefined
          }
          clearTestId="chip-category-clear"
        />

        <Chip
          testId="chip-book"
          label={selectedBook ? `帳本：${selectedBook.name}` : '帳本'}
          active={!!selectedBook}
          onClick={() => setOpenSheet('book')}
          onClear={
            selectedBook ? () => applyFilters({ bookId: null }) : undefined
          }
          clearTestId="chip-book-clear"
        />

        <Chip
          testId="chip-date-range"
          label={
            filters.dateRange
              ? `日期：${labelForDateRange(filters.dateRange)}`
              : '日期'
          }
          active={!!filters.dateRange}
          onClick={() => setOpenSheet('date_range')}
          onClear={
            filters.dateRange
              ? () => applyFilters({ dateRange: null })
              : undefined
          }
          clearTestId="chip-date-range-clear"
        />

        <Chip
          testId="chip-amount"
          label={
            filters.amount
              ? `金額：${labelForAmountOp(filters.amount.op)} ${formatAmount(
                  Math.round(filters.amount.amount * 100),
                  primaryCurrency,
                )}`
              : '金額'
          }
          active={!!filters.amount}
          onClick={() => setOpenSheet('amount')}
          onClear={
            filters.amount ? () => applyFilters({ amount: null }) : undefined
          }
          clearTestId="chip-amount-clear"
        />
      </div>

      <div
        data-testid="search-results"
        role="region"
        aria-live="polite"
        aria-label="搜尋結果"
        style={{ flex: 1, overflow: 'auto', padding: '4px 16px 24px' }}
      >
        {!hasAny && (
          <EmptyState
            recent={recent}
            saved={saved}
            transactions={transactions}
            filterCtx={filterCtx}
            onPickRecent={(q) => applyFilters({ query: q })}
            onClearRecent={handleClearRecent}
            onPickSaved={applySavedFilter}
            onDeleteSaved={handleDeleteSaved}
          />
        )}
        {hasAny && results.length === 0 && (
          <div
            style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: 'var(--fg-3)',
              font: '500 14px var(--font-cjk)',
            }}
          >
            沒有符合條件的紀錄
          </div>
        )}
        {hasAny && results.length > 0 && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                font: '400 12px var(--font-cjk)',
                color: 'var(--fg-3)',
                padding: '4px 0 8px',
              }}
            >
              <span>
                共 {results.length} 筆 ·{' '}
                {formatAmount(
                  results.reduce((acc, t) => acc + t.amount_minor, 0),
                  results[0]?.currency ?? 'TWD',
                )}
              </span>
              <button
                type="button"
                data-testid="save-filter-button"
                onClick={() => setOpenSheet('save_filter')}
                style={{
                  background: 'transparent',
                  border: '0.5px solid var(--border-1)',
                  borderRadius: 999,
                  padding: '4px 10px',
                  color: 'var(--accent)',
                  font: '500 12px var(--font-cjk)',
                  cursor: 'pointer',
                }}
              >
                儲存篩選
              </button>
            </div>
            <RecentTxList
              transactions={results}
              categories={categories}
              primaryCurrency={primaryCurrency}
              {...(onTxClick ? { onTxClick } : {})}
            />
          </>
        )}
      </div>

      {/* Category sheet */}
      <BottomSheet
        open={openSheet === 'category'}
        onClose={() => setOpenSheet(null)}
        title="選擇分類"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {categories
            .filter((c) => !c.deleted_at && !c.parent_id)
            .map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`cat-option-${c.id}`}
                onClick={() => {
                  applyFilters({ categoryId: c.id });
                  setOpenSheet(null);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 8px',
                  background: 'transparent',
                  border: 0,
                  borderRadius: 10,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 8,
                    background: cssColor(c.color_token),
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    font: '500 14px var(--font-cjk)',
                    color: 'var(--fg-1)',
                  }}
                >
                  {c.name}
                </span>
              </button>
            ))}
        </div>
      </BottomSheet>

      {/* Book sheet */}
      <BottomSheet
        open={openSheet === 'book'}
        onClose={() => setOpenSheet(null)}
        title="選擇帳本"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {books
            .filter((b) => !b.deleted_at)
            .map((b) => (
              <button
                key={b.id}
                type="button"
                data-testid={`book-option-${b.id}`}
                onClick={() => {
                  applyFilters({ bookId: b.id });
                  setOpenSheet(null);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 8px',
                  background: 'transparent',
                  border: 0,
                  borderRadius: 10,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    background: cssColor(b.color_token),
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    font: '500 14px var(--font-cjk)',
                    color: 'var(--fg-1)',
                  }}
                >
                  {b.name}
                </span>
              </button>
            ))}
        </div>
      </BottomSheet>

      {/* Date range sheet */}
      <BottomSheet
        open={openSheet === 'date_range'}
        onClose={() => setOpenSheet(null)}
        title="日期範圍"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(
            [
              ['this_month', '本月'],
              ['last_month', '上月'],
              ['past_3_months', '過去 3 個月'],
              ['custom', '自訂'],
            ] as [DateRangeKey, string][]
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              data-testid={`date-range-option-${k}`}
              onClick={() => {
                applyFilters({ dateRange: k });
                setOpenSheet(null);
              }}
              style={{
                padding: '12px',
                background: 'transparent',
                border: 0,
                borderRadius: 10,
                cursor: 'pointer',
                textAlign: 'left',
                font: '500 14px var(--font-cjk)',
                color: 'var(--fg-1)',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </BottomSheet>

      {/* Amount sheet */}
      <AmountSheet
        open={openSheet === 'amount'}
        initial={filters.amount}
        primaryCurrency={primaryCurrency}
        onClose={() => setOpenSheet(null)}
        onConfirm={(a) => {
          applyFilters({ amount: a });
          setOpenSheet(null);
        }}
      />

      {/* Save filter sheet */}
      <SaveFilterSheet
        open={openSheet === 'save_filter'}
        defaultName={suggestFilterName(filters, categories, books, primaryCurrency)}
        onClose={() => setOpenSheet(null)}
        onConfirm={handleSaveFilter}
      />
    </div>
  );
}

interface EmptyStateProps {
  recent: string[];
  saved: SavedFilter[];
  transactions: TransactionRow[];
  filterCtx: FilterContext;
  onPickRecent: (q: string) => void;
  onClearRecent: () => void;
  onPickSaved: (f: SavedFilter) => void;
  onDeleteSaved: (id: string) => void;
}

function EmptyState({
  recent,
  saved,
  transactions,
  filterCtx,
  onPickRecent,
  onClearRecent,
  onPickSaved,
  onDeleteSaved,
}: EmptyStateProps): JSX.Element {
  if (recent.length === 0 && saved.length === 0) {
    return (
      <div
        style={{
          padding: '40px 20px',
          textAlign: 'center',
          color: 'var(--fg-3)',
          font: '500 14px var(--font-cjk)',
        }}
      >
        請輸入關鍵字或選擇篩選條件
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 8 }}>
      {recent.length > 0 && (
        <section data-testid="recent-searches">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <h3
              style={{
                margin: 0,
                font: '600 14px var(--font-cjk)',
                color: 'var(--fg-1)',
              }}
            >
              最近搜尋
            </h3>
            <button
              type="button"
              data-testid="clear-recent"
              onClick={onClearRecent}
              style={{
                background: 'transparent',
                border: 0,
                color: 'var(--fg-3)',
                font: '500 12px var(--font-cjk)',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              清除
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {recent.map((q) => (
              <button
                key={q}
                type="button"
                data-testid={`recent-${q}`}
                onClick={() => onPickRecent(q)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 999,
                  border: '0.5px solid var(--border-1)',
                  background: 'var(--bg-surface)',
                  color: 'var(--fg-2)',
                  font: '500 13px var(--font-cjk)',
                  cursor: 'pointer',
                }}
              >
                <Icon name="search" size={13} stroke="var(--fg-3)" />
                {q}
              </button>
            ))}
          </div>
        </section>
      )}
      {saved.length > 0 && (
        <section data-testid="saved-filters">
          <h3
            style={{
              margin: '0 0 8px',
              font: '600 14px var(--font-cjk)',
              color: 'var(--fg-1)',
            }}
          >
            已儲存的篩選
          </h3>
          <div
            style={{
              background: 'var(--bg-surface)',
              borderRadius: 14,
              border: '0.5px solid var(--border-1)',
              overflow: 'hidden',
            }}
          >
            {saved.map((f, i) => {
              const count = filter(
                transactions,
                {
                  query: f.filters.query ?? '',
                  kind: f.filters.kind ?? 'all',
                  categoryId: f.filters.categoryId ?? null,
                  bookId: f.filters.bookId ?? null,
                  dateRange: f.filters.dateRange ?? null,
                  amount: f.filters.amount ?? null,
                },
                filterCtx,
              ).length;
              return (
                <div
                  key={f.id}
                  data-testid={`saved-${f.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    borderTop: i === 0 ? '0' : '0.5px solid var(--border-1)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onPickSaved(f)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      flex: 1,
                      minWidth: 0,
                      background: 'transparent',
                      border: 0,
                      cursor: 'pointer',
                      padding: 0,
                      textAlign: 'left',
                    }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: 'var(--bg-raised)',
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon name="filter" size={18} stroke="var(--fg-2)" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          font: '500 14px var(--font-cjk)',
                          color: 'var(--fg-1)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {f.name}
                      </div>
                      <div
                        style={{
                          font: '400 12px var(--font-cjk)',
                          color: 'var(--fg-3)',
                          marginTop: 2,
                        }}
                      >
                        {count} 筆符合
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    aria-label={`刪除 ${f.name}`}
                    data-testid={`saved-delete-${f.id}`}
                    onClick={() => onDeleteSaved(f.id)}
                    style={{
                      background: 'transparent',
                      border: 0,
                      cursor: 'pointer',
                      padding: 6,
                      color: 'var(--fg-3)',
                    }}
                  >
                    <Icon name="trash" size={16} stroke="var(--fg-3)" />
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

interface AmountSheetProps {
  open: boolean;
  initial: AmountFilter | null;
  primaryCurrency: string;
  onClose: () => void;
  onConfirm: (a: AmountFilter) => void;
}

function AmountSheet({
  open,
  initial,
  primaryCurrency,
  onClose,
  onConfirm,
}: AmountSheetProps): JSX.Element {
  const [op, setOp] = useState<AmountOp>(initial?.op ?? '>');
  const [amount, setAmount] = useState<string>(
    initial != null ? String(initial.amount) : '',
  );

  // Reset whenever the sheet re-opens (so a previously typed but
  // un-confirmed value doesn't leak into the next session).
  useEffect(() => {
    if (open) {
      setOp(initial?.op ?? '>');
      setAmount(initial != null ? String(initial.amount) : '');
    }
  }, [open, initial]);

  const numericAmount = Number(amount);
  const valid = amount.trim() !== '' && Number.isFinite(numericAmount) && numericAmount >= 0;

  return (
    <BottomSheet open={open} onClose={onClose} title="金額篩選">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div
          style={{
            font: '400 12px var(--font-cjk)',
            color: 'var(--fg-3)',
          }}
        >
          以主要幣別 {primaryCurrency} 比對 — 外幣紀錄會先換算後再比對。
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {AMOUNT_OPS.map(({ op: o, label }) => (
            <button
              key={o}
              type="button"
              data-testid={`amount-op-${o}`}
              onClick={() => setOp(o)}
              style={{
                padding: '6px 12px',
                borderRadius: 999,
                border:
                  op === o ? '0' : '0.5px solid var(--border-1)',
                background: op === o ? 'var(--accent)' : 'var(--bg-surface)',
                color: op === o ? '#fff' : 'var(--fg-2)',
                font: '500 13px var(--font-cjk)',
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          type="number"
          inputMode="decimal"
          aria-label="金額"
          placeholder="0"
          min={0}
          step="any"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 14px',
            border: '0.5px solid var(--border-1)',
            borderRadius: 12,
            background: 'var(--bg-surface)',
            font: '500 16px var(--font-num)',
            color: 'var(--fg-1)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: 12,
              background: 'var(--bg-raised)',
              border: 0,
              borderRadius: 12,
              font: '500 14px var(--font-cjk)',
              color: 'var(--fg-1)',
              cursor: 'pointer',
            }}
          >
            取消
          </button>
          <button
            type="button"
            data-testid="amount-confirm"
            disabled={!valid}
            onClick={() => valid && onConfirm({ op, amount: numericAmount })}
            style={{
              flex: 1,
              padding: 12,
              background: 'var(--accent)',
              border: 0,
              borderRadius: 12,
              font: '600 14px var(--font-cjk)',
              color: '#fff',
              cursor: valid ? 'pointer' : 'not-allowed',
              opacity: valid ? 1 : 0.5,
            }}
          >
            套用
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

interface SaveFilterSheetProps {
  open: boolean;
  defaultName: string;
  onClose: () => void;
  onConfirm: (name: string) => void;
}

function SaveFilterSheet({
  open,
  defaultName,
  onClose,
  onConfirm,
}: SaveFilterSheetProps): JSX.Element {
  const [name, setName] = useState(defaultName);

  useEffect(() => {
    if (open) setName(defaultName);
  }, [open, defaultName]);

  const trimmed = name.trim();

  return (
    <BottomSheet open={open} onClose={onClose} title="儲存篩選">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <input
          type="text"
          aria-label="篩選名稱"
          placeholder="例如：本月飲食"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 14px',
            border: '0.5px solid var(--border-1)',
            borderRadius: 12,
            background: 'var(--bg-surface)',
            font: '500 16px var(--font-cjk)',
            color: 'var(--fg-1)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: 12,
              background: 'var(--bg-raised)',
              border: 0,
              borderRadius: 12,
              font: '500 14px var(--font-cjk)',
              color: 'var(--fg-1)',
              cursor: 'pointer',
            }}
          >
            取消
          </button>
          <button
            type="button"
            data-testid="save-filter-confirm"
            disabled={!trimmed}
            onClick={() => trimmed && onConfirm(trimmed)}
            style={{
              flex: 1,
              padding: 12,
              background: 'var(--accent)',
              border: 0,
              borderRadius: 12,
              font: '600 14px var(--font-cjk)',
              color: '#fff',
              cursor: trimmed ? 'pointer' : 'not-allowed',
              opacity: trimmed ? 1 : 0.5,
            }}
          >
            儲存
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

function suggestFilterName(
  f: Filters,
  categories: CategoryRow[],
  books: BookRow[],
  primaryCurrency: string,
): string {
  if (f.query.trim()) return f.query.trim();
  const cat = f.categoryId
    ? categories.find((c) => c.id === f.categoryId)
    : null;
  if (cat) return cat.name;
  if (f.amount) {
    return `${labelForAmountOp(f.amount.op)} ${formatAmount(
      Math.round(f.amount.amount * 100),
      primaryCurrency,
    )}`;
  }
  const bk = f.bookId ? books.find((b) => b.id === f.bookId) : null;
  if (bk) return bk.name;
  if (f.dateRange) return labelForDateRange(f.dateRange);
  if (f.kind === 'expense') return '支出';
  if (f.kind === 'income') return '收入';
  return '';
}

function labelForDateRange(k: DateRangeKey): string {
  return k === 'this_month'
    ? '本月'
    : k === 'last_month'
      ? '上月'
      : k === 'past_3_months'
        ? '過去 3 個月'
        : '自訂';
}

function labelForAmountOp(o: AmountOp): string {
  switch (o) {
    case '>': return '>';
    case '>=': return '≥';
    case '=': return '=';
    case '<=': return '≤';
    case '<': return '<';
  }
}

interface ChipProps {
  testId: string;
  label: string;
  active: boolean;
  onClick: () => void;
  onClear?: () => void;
  clearTestId?: string;
}

function Chip({
  testId,
  label,
  active,
  onClick,
  onClear,
  clearTestId,
}: ChipProps): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: active ? '4px 4px 4px 12px' : '6px 12px',
        background: active ? 'var(--accent)' : 'var(--bg-surface)',
        color: active ? '#fff' : 'var(--fg-2)',
        border: active ? '0' : '0.5px solid var(--border-1)',
        borderRadius: 999,
        font: '500 12px var(--font-cjk)',
      }}
    >
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        style={{
          background: 'transparent',
          border: 0,
          padding: 0,
          cursor: 'pointer',
          color: 'inherit',
          font: 'inherit',
        }}
      >
        {label}
      </button>
      {active && onClear && (
        <button
          type="button"
          data-testid={clearTestId ?? `${testId}-clear`}
          aria-label="清除篩選"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          style={{
            width: 18,
            height: 18,
            display: 'inline-grid',
            placeItems: 'center',
            background: 'rgba(255,255,255,0.18)',
            border: 0,
            borderRadius: 999,
            cursor: 'pointer',
            color: '#fff',
            font: '600 10px var(--font-cjk)',
          }}
        >
          ×
        </button>
      )}
    </span>
  );
}

// Re-exports for tests / external composition.
export type { TxKind };
